// Wyszukiwanie produktu po kodzie kreskowym (EAN/UPC) w Open Food Facts.
// Zwraca dane gotowe do prefillu formularza produktu (makra na 100 g) lub null.
export interface BarcodeProduct {
    code: string
    name: string
    protein: number
    fat: number
    carbs: number
    sugar: number
    fiber: number
    saturated_fat: number
    salt: number
    calories: number | null
}

export interface ProductSearchResult extends BarcodeProduct {
    code: string
    brand: string
}

const round = (v: any): number => {
    const n = Number(v)
    return Number.isFinite(n) ? Math.round(n) : 0
}

// OFF podaje salt_100g w GRAMACH; w naszej bazie sól trzymamy w mg → ×1000.
const roundSaltMg = (v: any): number => round(Number(v) * 1000)

export const useBarcode = () => {
    const lookup = async (barcode: string): Promise<BarcodeProduct | null> => {
        const code = barcode.trim()
        if (!code) return null

        const res = await $fetch<any>(
            `https://world.openfoodfacts.org/api/v2/product/${encodeURIComponent(code)}.json`,
            { params: { fields: 'product_name,brands,nutriments' }, retry: 0 }
        )

        // status 0 = nie znaleziono
        if (!res || res.status !== 1 || !res.product) return null

        const p = res.product
        const n = p.nutriments ?? {}
        const name = [p.product_name, p.brands?.split(',')[0]?.trim()]
            .filter(Boolean)
            .join(' — ')

        return {
            code,
            name: name || p.product_name || '',
            protein: round(n.proteins_100g),
            fat: round(n.fat_100g),
            carbs: round(n.carbohydrates_100g),
            sugar: round(n.sugars_100g),
            fiber: round(n.fiber_100g),
            saturated_fat: round(n['saturated-fat_100g']),
            salt: roundSaltMg(n.salt_100g),
            calories: n['energy-kcal_100g'] != null ? round(n['energy-kcal_100g']) : null,
        }
    }

    // Wyszukiwanie po nazwie / marce.
    // Używamy cgi/search.pl, bo jako jedyny endpoint OFF z wyszukiwaniem tekstowym
    // wysyła nagłówki CORS (search-a-licious ich nie ma). Jest rate-limitowany i
    // bywa 503 — a jego odpowiedź 503 NIE ma nagłówka CORS, więc w przeglądarce to
    // błąd sieci bez statusu. Dlatego ponawiamy ręcznie, łapiąc dowolny błąd.
    const sleep = (ms: number) => new Promise(r => setTimeout(r, ms))

    const search = async (query: string): Promise<ProductSearchResult[]> => {
        const q = query.trim()
        if (!q) return []

        const attempts = 5
        let res: any = null
        let lastErr: any = null
        for (let i = 0; i < attempts; i++) {
            try {
                res = await $fetch<any>('https://world.openfoodfacts.org/cgi/search.pl', {
                    params: {
                        search_terms: q,
                        search_simple: 1,
                        action: 'process',
                        json: 1,
                        page_size: 20,
                        fields: 'code,product_name,brands,nutriments',
                    },
                    retry: 0,
                })
                break
            } catch (e) {
                lastErr = e
                if (i < attempts - 1) await sleep(500 * (i + 1)) // narastający odstęp
            }
        }
        if (!res) throw lastErr

        return (res?.products ?? [])
            .filter((p: any) => p.product_name)
            .map((p: any): ProductSearchResult => {
                const n = p.nutriments ?? {}
                // brands bywa stringiem z przecinkami lub tablicą — obsłuż oba
                const brand = (Array.isArray(p.brands) ? p.brands[0] : p.brands?.split(',')[0])?.trim() ?? ''
                return {
                    code: p.code ?? '',
                    brand,
                    name: [p.product_name, brand].filter(Boolean).join(' — '),
                    protein: round(n.proteins_100g),
                    fat: round(n.fat_100g),
                    carbs: round(n.carbohydrates_100g),
                    sugar: round(n.sugars_100g),
                    fiber: round(n.fiber_100g),
                    saturated_fat: round(n['saturated-fat_100g']),
                    salt: roundSaltMg(n.salt_100g),
                    calories: n['energy-kcal_100g'] != null ? round(n['energy-kcal_100g']) : null,
                }
            })
    }

    return { lookup, search }
}
